/**
 * CV-JD Analysis API service.
 */

import { createApiClient } from '@aaas/shared';
import type {
  AnalysisTriggerResponse,
  CVJDMapping,
  CVJDMappingListResponse,
  JDMappingsResponse,
} from '../types';

interface MappingApiResponse {
  success: boolean;
  data: CVJDMapping;
  message: string;
}

class AnalysisService {
  /**
   * Trigger CV-JD compatibility analysis.
   * POST /cvs/{cvId}/analyze/{jdId}
   */
  async triggerAnalysis(
    workspaceId: string,
    idToken: string,
    cvId: string,
    jdId: string,
  ): Promise<AnalysisTriggerResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<AnalysisTriggerResponse>(
      `/cvs/${cvId}/analyze/${jdId}`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } },
    );
    return response.data;
  }

  /**
   * Get single CV-JD analysis result.
   * GET /cvs/{cvId}/analysis/{jdId}
   */
  async getAnalysis(
    workspaceId: string,
    idToken: string,
    cvId: string,
    jdId: string,
  ): Promise<CVJDMapping> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<MappingApiResponse>(
      `/cvs/${cvId}/analysis/${jdId}`,
      { headers: { 'X-Workspace-ID': workspaceId } },
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get analysis');
    }
    return response.data.data;
  }

  /**
   * List all CV-JD mappings for a job (for history matrix).
   * GET /jobs/{jobId}/cv-jd-mappings
   */
  async listJobMappings(
    workspaceId: string,
    idToken: string,
    jobId: string,
  ): Promise<CVJDMappingListResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<CVJDMappingListResponse>(
      `/jobs/${jobId}/cv-jd-mappings`,
      { headers: { 'X-Workspace-ID': workspaceId } },
    );
    return response.data;
  }

  /**
   * Delete a CV-JD mapping record.
   * DELETE /jobs/{jobId}/cv-jd-mappings/{mappingId}
   */
  async deleteMapping(
    workspaceId: string,
    idToken: string,
    jobId: string,
    mappingId: string,
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/jobs/${jobId}/cv-jd-mappings/${mappingId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }

  /**
   * List JDs for a job with their CV mappings, paginated and searchable.
   * GET /jobs/{jobId}/cv-jd-mappings/by-jd
   */
  async listJobMappingsByJD(
    workspaceId: string,
    idToken: string,
    jobId: string,
    params: { page?: number; page_size?: number; search?: string },
  ): Promise<JDMappingsResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JDMappingsResponse>(
      `/jobs/${jobId}/cv-jd-mappings/by-jd`,
      { headers: { 'X-Workspace-ID': workspaceId }, params },
    );
    return response.data;
  }
}

export const analysisService = new AnalysisService();
export default analysisService;
